import { Star } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useAuth } from '../auth'
import { Button } from '../components/ui/Button'
import { Input, Label } from '../components/ui/Input'

export function LoginPage() {
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const result = await login(email.trim(), password)
    if (!result.ok) setError(result.error)
    setBusy(false)
  }

  return (
    <div className="min-h-screen w-full grid place-items-center px-4 relative overflow-hidden">
      {/* Extra ambient nebula glow behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 40%, rgba(34,211,238,0.18), transparent 70%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(167,139,250,0.16), transparent 70%)',
        }}
      />
      {/* Distant orbit ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 grid place-items-center"
      >
        <div className="size-[640px] rounded-full border border-cyan-500/10" />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 grid place-items-center"
      >
        <div className="size-[420px] rounded-full border border-violet-500/10" />
      </div>

      <div className="w-full max-w-sm animate-page-in">
        {/* Brand crest */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="relative">
            <div aria-hidden className="absolute inset-0 rounded-xl bg-cyan-500/40 blur-xl -z-10" />
            <div className="relative size-14 rounded-xl bg-gradient-to-br from-cyan-400 via-cyan-500 to-violet-600 grid place-items-center shadow-[0_0_0_1px_rgba(34,211,238,0.5)_inset,0_4px_24px_-4px_rgba(34,211,238,0.7)] overflow-hidden">
              <span
                aria-hidden
                className="absolute inset-0 opacity-70"
                style={{
                  backgroundImage:
                    'radial-gradient(1px 1px at 25% 30%, rgba(255,255,255,0.9), transparent 60%), radial-gradient(1.5px 1.5px at 70% 60%, rgba(255,255,255,0.8), transparent 60%), radial-gradient(1px 1px at 50% 80%, rgba(255,255,255,0.8), transparent 60%), radial-gradient(1px 1px at 85% 25%, rgba(255,255,255,0.7), transparent 60%)',
                }}
              />
              <Star
                className="relative size-7 text-zinc-50 fill-zinc-50/95 drop-shadow-[0_0_3px_rgba(255,255,255,0.7)]"
                strokeWidth={1.3}
              />
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <h1 className="font-display text-2xl font-semibold tracking-tight bg-gradient-to-r from-zinc-50 to-zinc-400 bg-clip-text text-transparent">
                Squadron
              </h1>
              <span className="size-1.5 rounded-full bg-cyan-400 pulse-cyan" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
              AI-orchestrated software house
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="relative rounded-xl border border-zinc-800 bg-zinc-900/70 backdrop-blur-md p-6 space-y-4 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7),0_0_0_1px_rgba(34,211,238,0.12)_inset,0_0_80px_-12px_rgba(34,211,238,0.25)]"
        >
          {/* Top cyan hairline */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent rounded-t-xl"
          />
          <div>
            <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-50">
              Accedi al ponte di comando
            </h2>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              Usa email e password per pilotare il tuo squadrone.
            </p>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@example.com"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md border border-pink-500/30 bg-pink-500/10 px-3 py-2 text-sm text-pink-200">
              {error}
            </div>
          )}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Allineamento orbitale…' : 'Avvia propulsori'}
          </Button>
        </form>

        <p className="text-[11px] text-zinc-600 text-center mt-6 tracking-wider uppercase">
          Squadron · v0.1 · ad astra
        </p>
      </div>
    </div>
  )
}
